import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import {
  collection, onSnapshot, addDoc, updateDoc,
  deleteDoc, doc, serverTimestamp, query, orderBy, getDoc, setDoc,
} from 'firebase/firestore';

const ProductStoreContext = createContext();

const VIEWED_KEY = 'techdave_recently_viewed';
const CART_KEY   = 'techdave_cart';
const ORDERS_KEY = 'techdave_orders';

export const CATEGORIES = [
  'Laptops', 'Phones', 'Desktops', 'Printers',
  'PA Systems', 'Networking', 'Accessories', 'Laptop Bags',
];

export const FOR_TAGS = ['Business', 'Executive', 'Students', 'Gaming'];

export const TECHDAVE_PHONES = [
  '263772758875',
  '263785661758',
];

const hashPin = async (pin) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(pin);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

const DEFAULT_PIN_HASH = '03ac674216f3e15c761ee1a5e255f067953623c8b388b4459e13f978d7c846f4';

const loadFromStorage = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch { return fallback; }
};

const saveToStorage = (key, data) => {
  try { localStorage.setItem(key, JSON.stringify(data)); } catch {}
};

export const buildWhatsAppMessage = (order) => {
  const itemLines = order.items
    .map(i => `  • ${i.name} x${i.qty} = USD ${(i.unitPrice * i.qty).toFixed(2)}`)
    .join('\n');

  const paymentLabel =
    order.paymentMethod === 'ecocash' ? '📱 EcoCash' :
    order.paymentMethod === 'swipe'   ? '💳 Swipe / Card' :
                                        '🏦 Online Transfer / ZIPIT';

  return (
    `🛒 *NEW ORDER — Techdave Solutions*\n` +
    `Order *#${order.id}*\n` +
    `📅 ${new Date(order.createdAt).toLocaleString()}\n\n` +
    `👤 *Customer:* ${order.customerName}\n` +
    `📞 *Phone:* ${order.customerPhone}\n` +
    `${order.address ? `📍 *Address:* ${order.address}\n` : ''}` +
    `💳 *Payment:* ${paymentLabel}\n` +
    `${order.ecocashNumber ? `   EcoCash No: ${order.ecocashNumber}\n` : ''}` +
    `\n🛍️ *Items:*\n${itemLines}\n\n` +
    `💰 *TOTAL: USD ${order.total.toFixed(2)}*\n\n` +
    `_Please confirm with customer and arrange payment._`
  );
};

export const notifyWhatsApp = (order, phoneIndex = 0) => {
  const phone = TECHDAVE_PHONES[phoneIndex];
  const msg   = buildWhatsAppMessage(order);
  window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
};

export const notifyBothWhatsApp = (order) => {
  notifyWhatsApp(order, 0);
  setTimeout(() => notifyWhatsApp(order, 1), 900);
};

export const ProductStoreProvider = ({ children }) => {
  const [products,        setProducts]        = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [recentlyViewed,  setRecentlyViewed]  = useState(() => loadFromStorage(VIEWED_KEY, []));
  const [cart,            setCart]            = useState(() => loadFromStorage(CART_KEY,   []));
  const [orders,          setOrders]          = useState(() => loadFromStorage(ORDERS_KEY, []));
  const [adminMode,       setAdminMode]       = useState(false);
  const [forTagBanners,   setForTagBanners]   = useState({});
  const [quoteRequests,   setQuoteRequests]   = useState([]);
  const [loadingQuoteRequests, setLoadingQuoteRequests] = useState(true);

  useEffect(() => {
    const q = query(collection(db, 'products'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setProducts(list);
      setLoadingProducts(false);
    }, (error) => {
      console.error('Firestore error:', error);
      setLoadingProducts(false);
    });
    return () => unsub();
  }, []);

  // ── Firestore "For" tag banner images listener ─────────────────
  // Each tag gets its OWN document: settings/forTagBanners_Business, etc.
  // This avoids Firestore's 1MB per-document limit.
  useEffect(() => {
    const unsubscribers = FOR_TAGS.map(tag =>
      onSnapshot(doc(db, 'settings', `forTagBanners_${tag}`), (snap) => {
        if (snap.exists()) {
          setForTagBanners(prev => ({ ...prev, [tag]: snap.data().items || [] }));
        } else {
          setForTagBanners(prev => ({ ...prev, [tag]: [] }));
        }
      }, (error) => {
        console.error(`forTagBanners_${tag} listener error:`, error);
      })
    );
    return () => unsubscribers.forEach(u => u());
  }, []);

  // ── Quote Requests listener ─────────────────────────────────────
  // Firestore-backed (not localStorage) so a request submitted by a
  // customer on their phone/browser shows up for the admin on a
  // completely different device.
  useEffect(() => {
    const q = query(collection(db, 'quoteRequests'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setQuoteRequests(list);
      setLoadingQuoteRequests(false);
    }, (error) => {
      console.error('Quote requests listener error:', error);
      setLoadingQuoteRequests(false);
    });
    return () => unsub();
  }, []);

  useEffect(() => { saveToStorage(VIEWED_KEY, recentlyViewed); }, [recentlyViewed]);
  useEffect(() => { saveToStorage(CART_KEY,   cart);           }, [cart]);
  useEffect(() => { saveToStorage(ORDERS_KEY, orders);         }, [orders]);

  const loginAdmin = useCallback(async (pin) => {
    try {
      const entered = await hashPin(pin);
      const snap    = await getDoc(doc(db, 'settings', 'admin'));
      const stored  = snap.exists() ? snap.data().pinHash : DEFAULT_PIN_HASH;
      if (entered === stored) {
        if (!snap.exists()) {
          await setDoc(doc(db, 'settings', 'admin'), { pinHash: DEFAULT_PIN_HASH });
        }
        setAdminMode(true);
        return { success: true };
      }
      return { success: false, error: 'Incorrect PIN.' };
    } catch (err) {
      console.error('Login error:', err);
      return { success: false, error: 'Connection error. Try again.' };
    }
  }, []);

  const changeAdminPin = useCallback(async (currentPin, newPin) => {
    if (newPin.length < 6) return { success: false, error: 'New PIN must be at least 6 characters.' };
    try {
      const currentHash = await hashPin(currentPin);
      const snap        = await getDoc(doc(db, 'settings', 'admin'));
      const stored      = snap.exists() ? snap.data().pinHash : DEFAULT_PIN_HASH;
      if (currentHash !== stored) return { success: false, error: 'Current PIN is incorrect.' };
      const newHash = await hashPin(newPin);
      await setDoc(doc(db, 'settings', 'admin'), { pinHash: newHash });
      return { success: true };
    } catch (err) {
      console.error('Change PIN error:', err);
      return { success: false, error: 'Failed to update PIN. Check your connection.' };
    }
  }, []);

  const logoutAdmin = () => setAdminMode(false);

  const addProduct = useCallback(async (product) => {
    const p = { ...product, createdAt: new Date().toISOString(), views: 0 };
    const docRef = await addDoc(collection(db, 'products'), p);
    return { id: docRef.id, ...p };
  }, []);

  const updateProduct = useCallback(async (id, updates) => {
    await updateDoc(doc(db, 'products', id), updates);
  }, []);

  const removeProduct = useCallback(async (id) => {
    await deleteDoc(doc(db, 'products', id));
    setCart(prev => prev.filter(i => i.productId !== id));
  }, []);

  const toggleOutOfStock = useCallback(async (id) => {
    const product = products.find(p => p.id === id);
    if (product) {
      await updateDoc(doc(db, 'products', id), { outOfStock: !product.outOfStock });
    }
  }, [products]);

  // ── "FOR" TAG SHOWCASE ITEMS ─────────────────────────────────
  // Each tag stored in its own Firestore doc (settings/forTagBanners_Business etc.)
  // so we never hit the 1MB single-document limit.
  const MAX_FOR_TAG_IMAGES = 9999;

  const setForTagItems = useCallback(async (tag, items) => {
    await setDoc(doc(db, 'settings', `forTagBanners_${tag}`), { items }, { merge: false });
  }, []);

  const addForTagItem = useCallback(async (tag, item) => {
    const current = forTagBanners?.[tag] || [];
    const next = [...current, item];
    await setDoc(doc(db, 'settings', `forTagBanners_${tag}`), { items: next }, { merge: false });
  }, [forTagBanners]);

  const updateForTagItem = useCallback(async (tag, index, updates) => {
    const current = forTagBanners?.[tag] || [];
    const next = current.map((item, i) => i === index ? { ...item, ...updates } : item);
    await setDoc(doc(db, 'settings', `forTagBanners_${tag}`), { items: next }, { merge: false });
  }, [forTagBanners]);

  const removeForTagItem = useCallback(async (tag, index) => {
    const current = forTagBanners?.[tag] || [];
    const next = current.filter((_, i) => i !== index);
    await setDoc(doc(db, 'settings', `forTagBanners_${tag}`), { items: next }, { merge: false });
  }, [forTagBanners]);

  const trackView = useCallback(async (productId) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      await updateDoc(doc(db, 'products', productId), { views: (product.views || 0) + 1 });
    }
    setRecentlyViewed(prev =>
      [productId, ...prev.filter(id => id !== productId)].slice(0, 10)
    );
  }, [products]);

  const addToCart = useCallback((productId, qty = 1) => {
    setCart(prev => {
      const existing = prev.find(i => i.productId === productId);
      if (existing) return prev.map(i => i.productId === productId ? { ...i, qty: i.qty + qty } : i);
      return [...prev, { productId, qty }];
    });
  }, []);

  const removeFromCart  = useCallback((productId) => setCart(prev => prev.filter(i => i.productId !== productId)), []);
  const updateCartQty   = useCallback((productId, qty) => {
    if (qty < 1) { removeFromCart(productId); return; }
    setCart(prev => prev.map(i => i.productId === productId ? { ...i, qty } : i));
  }, [removeFromCart]);
  const clearCart = useCallback(() => setCart([]), []);

  const placeOrder = useCallback((orderData) => {
    const order = { id: 'TD-' + Date.now().toString().slice(-6), createdAt: new Date().toISOString(), status: 'pending', ...orderData };
    setOrders(prev => [order, ...prev]);
    clearCart();
    return order;
  }, [clearCart]);

  const updateOrderStatus = useCallback((orderId, status) => setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status } : o)), []);
  const deleteOrder       = useCallback((orderId) => setOrders(prev => prev.filter(o => o.id !== orderId)), []);

  // ── Quote Requests (customer inquiries → admin-only inbox) ──────
  // Customer submits name/phone/email + what they're looking for.
  // Admin reviews in the Admin Panel and replies directly (WhatsApp,
  // phone, or by building a formal priced quotation from it).
  const submitQuoteRequest = useCallback(async (data) => {
    const request = {
      name:         data.name || '',
      phone:        data.phone || '',
      email:        data.email || '',
      requirements: data.requirements || '',
      budget:       data.budget || '',
      status:       'new', // new | contacted | closed
      createdAt:    serverTimestamp(),
    };
    const docRef = await addDoc(collection(db, 'quoteRequests'), request);
    return { id: docRef.id, ...request };
  }, []);

  const updateQuoteRequestStatus = useCallback(async (id, status) => {
    await updateDoc(doc(db, 'quoteRequests', id), { status });
  }, []);

  const deleteQuoteRequest = useCallback(async (id) => {
    await deleteDoc(doc(db, 'quoteRequests', id));
  }, []);

  const inStockProducts        = products.filter(p => !p.outOfStock);
  const outOfStockProducts     = products.filter(p => p.outOfStock);
  const promoProducts          = products.filter(p => p.isPromo && !p.outOfStock);
  const recentProducts         = [...products].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 8);
  const recentlyViewedProducts = recentlyViewed.map(id => products.find(p => p.id === id)).filter(Boolean);
  const cartItems              = cart.map(i => ({ ...i, product: products.find(p => p.id === i.productId) })).filter(i => i.product);
  const cartTotal              = cartItems.reduce((sum, i) => {
    const price = i.product.isPromo ? i.product.promoPrice : i.product.price;
    return sum + (parseFloat(price) * i.qty);
  }, 0);
  const cartCount       = cart.reduce((sum, i) => sum + i.qty, 0);
  const categoryStats   = CATEGORIES.map(cat => ({
    name: cat, total: products.filter(p => p.category === cat).length,
    inStock: products.filter(p => p.category === cat && !p.outOfStock).length,
    outOfStock: products.filter(p => p.category === cat && p.outOfStock).length,
  }));
  const pendingOrders   = orders.filter(o => o.status === 'pending');
  const paidOrders      = orders.filter(o => o.status === 'paid');
  const cancelledOrders = orders.filter(o => o.status === 'cancelled');
  const totalRevenue    = paidOrders.reduce((sum, o) => sum + o.total, 0);
  const newQuoteRequests = quoteRequests.filter(q => q.status === 'new');

  return (
    <ProductStoreContext.Provider value={{
      products, loadingProducts,
      inStockProducts, outOfStockProducts, promoProducts,
      recentProducts, recentlyViewedProducts, categoryStats,
      addProduct, updateProduct, removeProduct, toggleOutOfStock, trackView,
      cart, cartItems, cartTotal, cartCount,
      addToCart, removeFromCart, updateCartQty, clearCart,
      orders, pendingOrders, paidOrders, cancelledOrders, totalRevenue,
      placeOrder, updateOrderStatus, deleteOrder,
      quoteRequests, newQuoteRequests, loadingQuoteRequests,
      submitQuoteRequest, updateQuoteRequestStatus, deleteQuoteRequest,
      adminMode, loginAdmin, logoutAdmin, changeAdminPin,
      forTagBanners, setForTagItems, addForTagItem, updateForTagItem, removeForTagItem, MAX_FOR_TAG_IMAGES,
      CATEGORIES, FOR_TAGS,
    }}>
      {children}
    </ProductStoreContext.Provider>
  );
};

export const useStore = () => useContext(ProductStoreContext);
